/**
 * Eagerly writes the current preorder_text metafield value as a
 * "Preorder expected ship date" line item property whenever the cart renders.
 *
 * Triggered on page load and on every cart re-render (cart drawer open,
 * quantity change, etc.) via MutationObserver. Only calls /cart/change.js
 * for line items where the property is missing or stale. Fails silently —
 * a network error never affects the customer experience.
 *
 * Covers all checkout paths including Shop Pay and accelerated checkout.
 */

let isUpdating = false;

async function syncPreorderProperties() {
  if (isUpdating) return;

  const rows = [...document.querySelectorAll('[data-preorder-text]')];
  if (rows.length === 0) return;

  isUpdating = true;
  try {
    const cartResp = await fetch('/cart.js');
    const cart = await cartResp.json();

    const itemsToUpdate = rows.filter((row) => {
      const lineItem = cart.items.find((i) => i.key === row.dataset.key);
      return lineItem?.properties?.['PRE ORDER Ships by'] !== row.dataset.preorderText;
    });

    if (itemsToUpdate.length === 0) return;

    await Promise.all(
      itemsToUpdate.map((row) => {
        const lineItem = cart.items.find((i) => i.key === row.dataset.key);
        const existingProps = lineItem?.properties ?? {};

        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: row.dataset.key,
            properties: {
              ...existingProps,
              'PRE ORDER Ships by': row.dataset.preorderText,
            },
          }),
        });
      })
    );
  } catch {
    // Fail silently — property sync is best-effort.
  } finally {
    isUpdating = false;
  }
}

// Run on initial page load (covers cart page).
document.addEventListener('DOMContentLoaded', syncPreorderProperties);

// Re-run whenever the cart re-renders (covers cart drawer and all updates).
// Debounced so rapid DOM mutations collapse into a single run.
let debounceTimer;
new MutationObserver(() => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(syncPreorderProperties, 300);
}).observe(document.body, { childList: true, subtree: true });
